import { brand } from "../../config/theme";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

import { useUserStore } from "../../store/userStore";
import { useSessionCheck } from "../../hooks/useSessionCheck";
import { setLoggingOut } from "../../utils/tokenUtils";
import { logoutUser } from "../../pages/login/functions";

import { Header } from "../../components/base/Header";
import { AppsSidebar, useFilteredCategories } from "../../components/base/AppsSidebar";
import { AppSidebarMenu } from "../../components/base/AppSidebarMenu";
import { useTheme } from "../../context/ThemeContext";
import type { AppItem } from "../../components/base/AppsSidebar";

import { FUNCTIONALITIES, CARGO_MARKET_BASE } from "./config";
import { useAccess } from "../../hooks/useAccess";
import { isPlatformAdminUser } from "../../hooks/useIsReseller";
import { MeProvider } from "./exchange/useMe";
import { NotificationsBell } from "./exchange/NotificationsBell";
import { requestFCMToken } from "../../firebaseMessaging";

// Single shell for every Cargo Market module. Derives the active functionality
// from the URL (/cargo-market/<slug>/...), renders that functionality's
// sub-menu, and the matched section via <Outlet/>. Mirrors NuvexAccessLayout
// (Header + AppSidebarMenu + AppsSidebar + Outlet). Comparte entre pantallas el
// estado «mi compañía en el mercado» (MeProvider) y la campana de avisos.
export default function CargoMarketLayout() {
  const { t } = useTranslation("common");
  const { canAccessSectionOfApp } = useAccess();
  const { themeMode, setTheme } = useTheme();
  const filteredCategories = useFilteredCategories();
  const user = useUserStore((state) => state.user);
  const hasRedirected = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  const { isExpiringSoon, timeRemainingFormatted, isExpired, isAuthenticated, isInitialized } =
    useSessionCheck({ warningThreshold: 60 * 60 * 1000, autoRefresh: true });

  const [isAppsSidebarOpen, setIsAppsSidebarOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Active functionality from URL: /cargo-market/<slug>/<item>
  const parts = location.pathname.split("/").filter(Boolean); // ["cargo-market", slug, item]
  const activeSlug = parts[1] ?? FUNCTIONALITIES[0].slug;
  // Administración del mercado: sólo plataforma (el API lo vuelve a exigir).
  const visible = FUNCTIONALITIES.filter((f) => !f.requiredRoleIds || isPlatformAdminUser());
  const functionality =
    visible.find((f) => f.slug === activeSlug) ?? visible[0];

  const openAppsSidebar = () => setIsAppsSidebarOpen(true);
  const closeAppsSidebar = () => setIsAppsSidebarOpen(false);

  const handleAppSelect = (app: AppItem) => {
    if (app.isComingSoon) {
      Swal.fire({
        title: t("appsSidebar.comingSoon"),
        text: t("appsSidebar.comingSoonText", { name: app.name }),
        icon: "info",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }
    if (app.isLocked) {
      Swal.fire({
        title: t("appsSidebar.premiumFeature"),
        text: t("appsSidebar.premiumRequired", { name: app.name }),
        icon: "warning",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }
    if (app.route) {
      closeAppsSidebar();
      navigate(app.route);
    } else if (app.externalUrl) {
      window.open(app.externalUrl, "_blank");
    }
  };

  const menuItems = functionality.menu
    .filter((item) =>
      canAccessSectionOfApp(`cargo-market-${functionality.slug}`, item.id),
    )
    .map((item) => ({
    id: item.id,
    label: item.label,
    icon: item.icon,
    path: `${CARGO_MARKET_BASE}/${functionality.slug}/${item.id}`,
  }));

  useEffect(() => {
    if (isInitialized && (isExpired || !isAuthenticated) && !hasRedirected.current) {
      hasRedirected.current = true;
      setLoggingOut(false);
      navigate("/");
    }
  }, [isExpired, isAuthenticated, isInitialized, navigate]);

  useEffect(() => {
    if (!user && location.pathname !== "/splash") {
      localStorage.setItem("lastRoute", location.pathname);
      navigate("/splash");
    }
  }, [user, location.pathname, navigate]);

  // Push del navegador: los avisos del Cargo Market (ofertas, viajes,
  // incidencias…) llegan por FCM aunque esta pestaña no esté abierta. Mismo
  // registro que hace el layout Lite; sin él, quien sólo usa el mercado no
  // tendría token.
  useEffect(() => {
    if (user?.id) requestFCMToken({ id: user.id });
  }, [user?.id]);

  return (
    <div className="h-full bg-app">
      {isExpiringSoon && (
        <div className="bg-amber-500 text-white px-4 py-2 text-center text-sm flex items-center justify-center gap-2">
          <i className="ri-alarm-warning-line"></i>
          <span>{t("sessionBanner.expiring", { time: timeRemainingFormatted })}</span>
        </div>
      )}

      <Header
        title="Cargo Market"
        subtitle={functionality.name}
        onOpenAppsSidebar={openAppsSidebar}
        isAppsSidebarOpen={isAppsSidebarOpen}
        customActions={<NotificationsBell />}
        />

      <div className="flex h-full">
        <AppSidebarMenu
          menuItems={menuItems}
          isMobileMenuOpen={false}
          onCloseMobileMenu={() => {}}
          onNavigate={(path) => navigate(path)}
          isActiveRoute={(itemId) =>
            location.pathname === `${CARGO_MARKET_BASE}/${functionality.slug}/${itemId}`
          }
          footerStats={{
            label: functionality.name,
            items: [
              { label: "Secciones", value: functionality.menu.length },
              { label: "Módulos", value: visible.length, className: "text-primary-500" },
            ],
          }}
        />

        <div className="flex-1 overflow-hidden flex justify-center w-full">
          <MeProvider>
            <Outlet />
          </MeProvider>
        </div>
      </div>

      <AppsSidebar
        isOpen={isAppsSidebarOpen}
        onClose={closeAppsSidebar}
        categories={filteredCategories}
        selectedAppId={`cargo-market-${functionality.slug}`}
        onAppSelect={handleAppSelect}
        onStoreClick={() => navigate("/store")}
        currentTheme={themeMode}
        onThemeChange={setTheme}
        userInfo={
          user
            ? {
                first_name: user.first_name,
                last_name: user.last_name,
                email: user.email,
                username: user.username,
              }
            : undefined
        }
        onProfileClick={() => {
          closeAppsSidebar();
          navigate("/profile");
        }}
        onLogout={async () => {
          setIsLoggingOut(true);
          await logoutUser();
          setIsLoggingOut(false);
        }}
        isLoggingOut={isLoggingOut}
      />
    </div>
  );
}
